/**
 * useTariffTree — singleton cache for the full HS tariff tree.
 *
 * Loads all 17K nodes once from /api/v2/tariff/tree/full, builds O(1) indexes,
 * and provides functions to compute visible tree for any given node.
 * Navigation, filtering, ancestor highlighting — all client-side, instant.
 */
import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

// ── Module-level singleton (shared across all component instances) ──

let _treePromise = null;
let _treeData = null; // { nodeById, childrenByParent, codeToId, roots }

const TREE_STORAGE_KEY = 'customs-search:tariff-tree:v1';
const TREE_STORAGE_TTL_MS = 12 * 60 * 60 * 1000;

function getSessionStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const storage = window.sessionStorage;
    const probe = '__tariff_tree_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

function readCachedTreePayload({ allowStale = false } = {}) {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(TREE_STORAGE_KEY);
    if (!raw) return null;

    const cached = JSON.parse(raw);
    if (!cached || !Array.isArray(cached.nodes) || typeof cached.cachedAt !== 'number') {
      storage.removeItem(TREE_STORAGE_KEY);
      return null;
    }

    const isFresh = Date.now() - cached.cachedAt < TREE_STORAGE_TTL_MS;
    if (!isFresh && !allowStale) return null;

    return { nodes: cached.nodes };
  } catch {
    storage.removeItem(TREE_STORAGE_KEY);
    return null;
  }
}

function writeCachedTreePayload(result) {
  const storage = getSessionStorage();
  if (!storage || !Array.isArray(result?.nodes)) return;

  try {
    storage.setItem(TREE_STORAGE_KEY, JSON.stringify({
      cachedAt: Date.now(),
      nodes: result.nodes,
    }));
  } catch {
    // Storage quota / private mode should not break tariff navigation.
  }
}

function buildIndexes(rawNodes) {
  const nodeById = new Map();          // hs_id -> node
  const childrenByParent = new Map();  // parent_id -> node[] (sorted by sort_order)
  const codeToId = new Map();          // code -> hs_id

  // First pass: create node objects, populate nodeById and codeToId
  for (const raw of rawNodes) {
    const node = {
      hs_id: raw.id,
      parent_id: raw.pid,
      level: raw.lv,
      code: raw.c,
      name: raw.n,
      duty_display: raw.d,
      unit: raw.u,
      unit_additional: raw.ua,
      sort_order: raw.so,
    };
    nodeById.set(node.hs_id, node);
    if (node.code) {
      codeToId.set(node.code, node.hs_id);
    }
  }

  // Second pass: group children by parent_id (already sorted by sort_order from backend)
  for (const raw of rawNodes) {
    const pid = raw.pid;
    if (!childrenByParent.has(pid)) {
      childrenByParent.set(pid, []);
    }
    childrenByParent.get(pid).push(nodeById.get(raw.id));
  }

  // Compute has_children for each node
  for (const [, node] of nodeById) {
    const kids = childrenByParent.get(node.hs_id);
    node.has_children = !!(kids && kids.length > 0);
  }

  // Root nodes = children of null parent
  const roots = childrenByParent.get(null) || [];

  return { nodeById, childrenByParent, codeToId, roots };
}

function fetchTree() {
  if (_treeData) return Promise.resolve(_treeData);
  if (_treePromise) return _treePromise;

  const cached = readCachedTreePayload();
  if (cached) {
    _treeData = buildIndexes(cached.nodes);
    return Promise.resolve(_treeData);
  }

  _treePromise = api.getFullTariffTree()
    .then(result => {
      writeCachedTreePayload(result);
      _treeData = buildIndexes(result.nodes);
      return _treeData;
    })
    .catch(err => {
      _treePromise = null;
      const stale = readCachedTreePayload({ allowStale: true });
      if (stale) {
        _treeData = buildIndexes(stale.nodes);
        return _treeData;
      }
      throw err;
    });
  return _treePromise;
}

export function prefetchTariffTree() {
  return fetchTree();
}

// ── Helper: get ancestor path from node up to root ──

function getAncestorPathFromTree(tree, nodeId) {
  const path = [];
  let current = tree.nodeById.get(nodeId);
  while (current) {
    path.unshift(current);
    current = current.parent_id != null ? tree.nodeById.get(current.parent_id) : null;
  }
  return path;
}

// ── Hook ──

export function useTariffTree() {
  const [tree, setTree] = useState(_treeData);
  const [loading, setLoading] = useState(!_treeData);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_treeData) {
      setTree(_treeData);
      setLoading(false);
      return;
    }
    fetchTree()
      .then(data => { setTree(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // Look up node by code string
  const getNodeByCode = useCallback((code) => {
    if (!tree || !code) return null;
    const id = tree.codeToId.get(code);
    return id != null ? tree.nodeById.get(id) : null;
  }, [tree]);

  // Look up node by hs_id
  const getNodeById = useCallback((hsId) => {
    if (!tree) return null;
    return tree.nodeById.get(hsId) || null;
  }, [tree]);

  /**
   * Build the visible tree for a given current node.
   *
   * Replicates the backend logic:
   * - For each ancestor level, show ALL siblings (children of parent)
   * - For the current node, show its direct children
   * - Mark is_ancestor, is_current, expanded flags
   *
   * Returns flat array of nodes (same shape as current API response `tree` field).
   */
  const buildVisibleNodes = useCallback((currentNodeId) => {
    if (!tree) return [];

    const ancestorPath = getAncestorPathFromTree(tree, currentNodeId);
    const ancestorIds = new Set(ancestorPath.map(n => n.hs_id));

    const flatNodes = [];
    const seen = new Set();

    // For each ancestor, get ALL its siblings (children of its parent)
    for (const anc of ancestorPath) {
      const parentId = anc.parent_id;
      const siblings = tree.childrenByParent.get(parentId) || [];
      for (const s of siblings) {
        if (seen.has(s.hs_id)) continue;
        seen.add(s.hs_id);
        const isAnc = ancestorIds.has(s.hs_id);
        const isCur = s.hs_id === currentNodeId;
        flatNodes.push({
          hs_id: s.hs_id,
          parent_id: s.parent_id,
          level: s.level,
          code: s.code,
          name: s.name,
          duty_display: s.duty_display,
          unit: s.unit,
          has_children: s.has_children,
          is_ancestor: isAnc && !isCur,
          is_current: isCur,
          expanded: isAnc,
        });
      }
    }

    // Children of current node
    const children = tree.childrenByParent.get(currentNodeId) || [];
    for (const c of children) {
      if (seen.has(c.hs_id)) continue;
      seen.add(c.hs_id);
      flatNodes.push({
        hs_id: c.hs_id,
        parent_id: c.parent_id,
        level: c.level,
        code: c.code,
        name: c.name,
        duty_display: c.duty_display,
        unit: c.unit,
        has_children: c.has_children,
        is_ancestor: false,
        is_current: false,
        expanded: false,
      });
    }

    return flatNodes;
  }, [tree]);

  /**
   * Build "ancestors only" view — clean staircase path.
   *
   * Shows ONLY the ancestor path from root to current node,
   * plus direct children of the current node.
   * No sibling nodes at any level — just the clean "ladder".
   */
  const buildAncestorOnlyNodes = useCallback((currentNodeId) => {
    if (!tree) return [];

    const ancestorPath = getAncestorPathFromTree(tree, currentNodeId);

    const flatNodes = [];

    // Each ancestor as a single node (no siblings)
    for (const anc of ancestorPath) {
      const isCur = anc.hs_id === currentNodeId;
      flatNodes.push({
        hs_id: anc.hs_id,
        parent_id: anc.parent_id,
        level: anc.level,
        code: anc.code,
        name: anc.name,
        duty_display: anc.duty_display,
        unit: anc.unit,
        has_children: anc.has_children,
        is_ancestor: !isCur,
        is_current: isCur,
        expanded: true,
      });
    }

    // Children of current node
    const children = tree.childrenByParent.get(currentNodeId) || [];
    for (const c of children) {
      flatNodes.push({
        hs_id: c.hs_id,
        parent_id: c.parent_id,
        level: c.level,
        code: c.code,
        name: c.name,
        duty_display: c.duty_display,
        unit: c.unit,
        has_children: c.has_children,
        is_ancestor: false,
        is_current: false,
        expanded: false,
      });
    }

    return flatNodes;
  }, [tree]);

  /**
   * Build explorer-mode tree: shows all root nodes, and expands only
   * the nodes whose IDs are in the expandedSet. Current node gets
   * ancestor highlighting. User controls expand/collapse manually.
   */
  const buildExplorerNodes = useCallback((currentNodeId, expandedSet) => {
    if (!tree) return [];

    const ancestorPath = currentNodeId ? getAncestorPathFromTree(tree, currentNodeId) : [];
    const ancestorIds = new Set(ancestorPath.map(n => n.hs_id));

    const flatNodes = [];

    function collectLevel(parentId) {
      const children = tree.childrenByParent.get(parentId) || [];
      for (const c of children) {
        const isAnc = ancestorIds.has(c.hs_id);
        const isCur = c.hs_id === currentNodeId;
        const isExpanded = expandedSet.has(c.hs_id);
        flatNodes.push({
          hs_id: c.hs_id,
          parent_id: c.parent_id,
          level: c.level,
          code: c.code,
          name: c.name,
          duty_display: c.duty_display,
          unit: c.unit,
          has_children: c.has_children,
          is_ancestor: isAnc && !isCur,
          is_current: isCur,
          expanded: isExpanded,
        });
        if (isExpanded) {
          collectLevel(c.hs_id);
        }
      }
    }

    collectLevel(null);
    return flatNodes;
  }, [tree]);

  /**
   * Build full nested tree from all roots (for global search).
   * Returns nested structure with children arrays.
   * Memoize externally — this is expensive for 17K nodes.
   */
  const buildFullNestedTree = useCallback(() => {
    if (!tree) return [];

    function buildSubtree(nodeId) {
      const node = tree.nodeById.get(nodeId);
      const kids = tree.childrenByParent.get(nodeId) || [];
      return {
        ...node,
        is_ancestor: false,
        is_current: false,
        expanded: false,
        children: kids.map(c => buildSubtree(c.hs_id)),
      };
    }

    return tree.roots.map(r => buildSubtree(r.hs_id));
  }, [tree]);

  // Expose ancestor path helper for external use
  const getAncestorPath = useCallback((nodeId) => {
    if (!tree) return [];
    return getAncestorPathFromTree(tree, nodeId);
  }, [tree]);

  return {
    tree,
    loading,
    error,
    getNodeByCode,
    getNodeById,
    getAncestorPath,
    buildVisibleNodes,
    buildAncestorOnlyNodes,
    buildExplorerNodes,
    buildFullNestedTree,
  };
}
